import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export function SelectionBar({ count, groupName, onGroupNameChange, onSave, onClear }) {
  const { t } = useTranslation(['planning', 'common']);
  if (count < 2) return null;
  return (
    <div className="wv-selection-bar fixed bottom-6 left-1/2 -translate-x-1/2 z-[75] flex items-center gap-3 px-4 py-3 bg-surface border border-border rounded-2xl shadow-[0_8px_24px_rgba(18,65,145,0.18)]">
      <span className="text-xs font-semibold text-primary whitespace-nowrap">{t('selectedCount', { count })}</span>
      <input
        type="text"
        maxLength={60}
        placeholder={t('groupName')}
        value={groupName}
        onChange={(e) => onGroupNameChange?.(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSave?.();
          }
        }}
        className="group-name-input w-44 px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-main placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-nokia-blue/20 focus:border-nokia-blue transition-all"
      />
      <button
        type="button"
        onClick={onSave}
        className="px-3 py-2 bg-primary hover:bg-primary-hover text-white text-xs font-medium rounded-lg active:scale-[0.98] transition-all whitespace-nowrap"
      >
        {t('saveGroup')}
      </button>
      <button
        type="button"
        onClick={onClear}
        className="px-3 py-2 text-xs font-medium text-text-secondary hover:text-text-main hover:bg-background-light rounded-lg transition-colors"
      >
        {t('common:clear')}
      </button>
    </div>
  );
}

export function PasteGroupBanner({ label, onCancel }) {
  if (!label) return null;
  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[75] flex items-center gap-3 px-4 py-2.5 bg-primary text-white rounded-xl shadow-[0_8px_24px_rgba(18,65,145,0.28)]">
      <span className="material-symbols-outlined text-[18px]">content_paste</span>
      <span className="text-xs font-medium">{label}</span>
      <button
        type="button"
        onClick={onCancel}
        className="px-2.5 py-1 text-[11px] font-semibold bg-white/15 hover:bg-white/25 rounded-lg transition-colors"
      >
        Esc
      </button>
    </div>
  );
}

export function GroupsDropdown({ open, groups, onApply, onDelete, onClose }) {
  const { t } = useTranslation('planning');
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (panelRef.current?.contains(target)) return;
      if (target.closest('[data-weekly-groups-toggle]')) return;
      onClose?.();
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('touchstart', onPointerDown);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div ref={panelRef} className="weekly-view-groups-dropdown">
      <div className="px-3 py-2.5 border-b border-border">
        <p className="text-xs font-bold text-primary">{t('savedGroups')}</p>
        <p className="text-[10px] text-text-secondary mt-0.5">{t('applyTemplateHint')}</p>
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {groups.length === 0 ? (
          <p className="px-3 py-4 text-xs text-text-secondary text-center">
            {t('noGroupsYet')}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.id} className="flex items-center gap-1 px-2 py-1.5 hover:bg-background-light">
              <button
                type="button"
                className="flex-1 text-left px-2 py-1.5 text-xs font-medium text-text-main truncate apply-group-btn"
                onClick={() => onApply?.(group)}
              >
                {group.name}
              </button>
              <button
                type="button"
                className="p-1 text-text-secondary hover:text-red-600 rounded transition-colors"
                aria-label={t('deleteGroup')}
                onClick={() => onDelete?.(group.id)}
              >
                <span className="material-symbols-outlined text-[16px]">delete</span>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
